import {
  AcceptedCurrencyCode,
  MarketCode,
} from '../../types/__generated-graphQL__';
import React, {
  FunctionComponent,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { AuthContext } from '@justgiving/auth-react';
import config from '../config';
import { setCharityBrandingColours } from '../utils/setCharityBrandingColours';

export type CharityApiResponse = {
  name: string;
  displayName: string;
  registrationNumber: string;
  operatingCompanyCode: MarketCode;
  emailAddress: string;
  webAddress: string;
  description: string;
  thankYouMessage: string;
  logoImageUrl: string;
  headerLogoImageUrl: string;
  profileWhat: string;
  profileWhy: string;
  giftAidEnabled: boolean;
  charityToken: string;
  address: {
    lines: string;
    town: string;
    county: string;
    postcode: string;
    country: string;
  };
  themeColour: string;
  settlementCurrencyCode: AcceptedCurrencyCode;
  bbmsLiveForJG: boolean;
  isApproved: boolean;
};

type ContextState = {
  charityId?: string;
  charity?: CharityApiResponse & { isMonthlyDisabled: boolean };
  loading: boolean;
};

const Context = createContext<ContextState | null>(null);

export const useCharityData = (): ContextState => {
  const contextState = useContext(Context);
  if (contextState === null) {
    throw new Error(
      'useCharityData must be used within a CharityDataProvider tag',
    );
  }
  return contextState;
};

type CharityDataProviderProps = {
  children: React.ReactNode;
};

export const CharityDataProvider: FunctionComponent<
  CharityDataProviderProps
> = ({ children }) => {
  const [charityState, setCharityState] = useState<ContextState['charity']>();
  const [loading, setLoading] = useState(true);
  const auth = useContext(AuthContext);
  const charityId = auth?.getActivePermission('charity')?.groupTypeExternalId;

  useEffect(() => {
    addCharityIdToDataLayer(
      auth?.getActivePermission('charity')?.groupTypeExternalId,
    );
    (async (): Promise<void> => {
      try {
        await auth?.refreshAccessTokenIfExpired();
        const response = await fetch(config.charityApi, {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${auth?.getParsedAuthCookie()?.accessToken}`,
          },
        });
        if (response.ok) {
          const json: CharityApiResponse = await response.json();
          setCharityBrandingColours(json.themeColour);
          const isMonthlyDisabled = [MarketCode.Nz].includes(
            json.operatingCompanyCode,
          );
          setCharityState({ ...json, isMonthlyDisabled });
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        // Do nothing
      }
    })();
  }, [auth]);

  return (
    <Context.Provider value={{ charity: charityState, charityId, loading }}>
      {children}
    </Context.Provider>
  );
};

const addCharityIdToDataLayer = (charityId: string | undefined) => {
  if (charityId) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'charityIdAvailable',
      charityId: charityId,
    });
  }
};
